// WebGL FBM domain-warped fog background, used for the title screen and the chat panel.

const VERTEX_SHADER = `attribute vec2 a_pos;void main(){gl_Position=vec4(a_pos,0,1);}`;

const FRAGMENT_SHADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform float u_time;
uniform vec2 u_res;

float hash(vec2 p) {
    p = fract(p * vec2(443.897, 397.297));
    p += dot(p, p.yx + 19.19);
    return fract(p.x * p.y);
}

float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i), b = hash(i + vec2(1, 0));
    float c = hash(i + vec2(0, 1)), d = hash(i + vec2(1, 1));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    mat2 rot = mat2(0.8, -0.6, 0.6, 0.8);
    for (int i = 0; i < 5; i++) {
        v += a * noise(p);
        p = rot * p * 2.0;
        a *= 0.5;
    }
    return v;
}

void main() {
    vec2 uv = gl_FragCoord.xy / u_res;
    float t = u_time * 0.12;

    // domain warping — fbm feeding into fbm for organic fog
    float w1 = fbm(uv * 2.5 + vec2(t * 0.8, t * 0.6));
    float w2 = fbm(uv * 2.5 + vec2(-t * 0.6, t * 0.9) + w1 * 2.0);
    float w3 = fbm(uv * 3.0 + vec2(t * 0.5, -t * 0.55) + w2 * 1.5);

    // deep dark base, purple fog, pink wisps
    vec3 dark   = vec3(0.035, 0.008, 0.08);
    vec3 purple = vec3(0.40, 0.18, 0.70);
    vec3 pink   = vec3(0.70, 0.15, 0.45);
    vec3 deep   = vec3(0.15, 0.06, 0.40);

    vec3 col = dark;
    col = mix(col, deep,   smoothstep(0.1, 0.65, w1) * 0.65);
    col = mix(col, purple, smoothstep(0.15, 0.8, w2) * 0.45);
    col = mix(col, pink,   smoothstep(0.4, 0.95, w3) * 0.25);

    // soft vignette
    float vig = 1.0 - 0.30 * length((uv - 0.5) * vec2(1.2, 1.0));
    col *= vig;

    gl_FragColor = vec4(col, 1.0);
}`;

function compile(gl: WebGLRenderingContext, src: string, type: number): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) console.error(gl.getShaderInfoLog(shader));
  return shader;
}

export function initFog(canvas: HTMLCanvasElement | null): void {
  if (!canvas) return;
  const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
  if (!gl) return;

  const dpr = Math.min(window.devicePixelRatio || 1, 1.25);
  const isMain = canvas.id === 'particle-canvas';
  function resize() {
    canvas!.width = Math.round((isMain ? window.innerWidth : canvas!.offsetWidth) * dpr);
    canvas!.height = Math.round((isMain ? window.innerHeight : canvas!.offsetHeight) * dpr);
    gl!.viewport(0, 0, canvas!.width, canvas!.height);
  }
  resize();
  if (isMain) window.addEventListener('resize', resize);

  const prog = gl.createProgram()!;
  gl.attachShader(prog, compile(gl, VERTEX_SHADER, gl.VERTEX_SHADER));
  gl.attachShader(prog, compile(gl, FRAGMENT_SHADER, gl.FRAGMENT_SHADER));
  gl.linkProgram(prog);
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const posAttr = gl.getAttribLocation(prog, 'a_pos');
  gl.enableVertexAttribArray(posAttr);
  gl.vertexAttribPointer(posAttr, 2, gl.FLOAT, false, 0, 0);

  const uTime = gl.getUniformLocation(prog, 'u_time');
  const uRes = gl.getUniformLocation(prog, 'u_res');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let animationFrame = 0;
  let lastDraw = -Infinity;
  function frame(t: number) {
    animationFrame = 0;
    if (document.hidden) return;
    if (!reducedMotion && t - lastDraw < 33) {
      animationFrame = requestAnimationFrame(frame);
      return;
    }
    lastDraw = t;
    gl!.uniform1f(uTime, t * 0.001);
    gl!.uniform2f(uRes, canvas!.width, canvas!.height);
    gl!.drawArrays(gl!.TRIANGLE_STRIP, 0, 4);
    if (!reducedMotion) animationFrame = requestAnimationFrame(frame);
  }
  animationFrame = requestAnimationFrame(frame);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && !animationFrame && !reducedMotion) {
      animationFrame = requestAnimationFrame(frame);
    }
  });
}

declare global {
  interface Window {
    _initFog: typeof initFog;
  }
}

// Exposed on window: the legacy inline script (index.html) still calls this
// as a bare global (`_initFog(...)`) to re-init the chat panel's fog canvas.
window._initFog = initFog;
